import { z } from 'zod';
import { EvidenceCitationSchema, LocatorSchema } from './locator';
import { NonEmptyStringSchema } from './primitives';

/**
 * What the identity worker is asked and what it may answer
 * (`docs/IDENTITY_WORKER.md`, docs/DECISIONS.md ZD-026, ZD-045, ZD-047).
 *
 * ## Two jobs, and they do not share a call shape
 *
 * `IDENTITY_WORKER.md` §8 closes by saying the resolution case *"takes the same
 * proposal shape, with `right` being a reference rather than an object"* — one
 * pairwise call, one four-member verdict enum. ZD-047 §5 says the worker is
 * *"offered a candidate list"*. They cannot both hold, and the pairwise form is
 * the one that breaks:
 *
 * > **A pairwise resolution call cannot express `no_object`.** Asked *"is this
 * > reference this candidate?"*, a model can only answer *not this one*.
 * > `no_object` is a claim about **every** candidate having been seen, so it is
 * > unwritable from a call that saw one — and ZD-047 §1 measured that it is the
 * > right answer for **three of the six** unresolved references in the corpus.
 *
 * That is ZD-019's question run on a verdict enum, which is exactly what
 * ZD-047 §5 said it was: *what does the worker write when nothing here is it?*
 * For a pairwise call the honest answer is *it has to put something*. So there
 * are two schemas, each exhaustive over its own job, rather than one enum with
 * two members that are meaningless in each direction.
 *
 * ## Four things a proposal may not carry, each because something else owns it
 *
 * None of them is refused by a rule; each is absent from the shape, which is
 * the only form of refusal a schema can actually make.
 *
 *  - **No confidence.** CLAUDE.md principle 5, ZD-013 §4. It is computed.
 *  - **No `basis`.** Code writes `inferred`, unconditionally. A model-proposed
 *    identity is never `labelled` — corpus rule A applied to a merge, the same
 *    call ZD-039 §2 made for control kinds and ZD-040 §7 for flow
 *    authentication.
 *  - **No merged values.** The worker decides *whether*; ZD-026 §5 decides what
 *    a merged fact carries, and it already handles agreement, absence-markers
 *    and disagreement.
 *  - **No new objects.** A proposal is about things that exist. `no_object` is
 *    the honest way to say a reference names none of them, and it routes to
 *    ZD-027 rule 1's drop-and-ask rather than minting anything.
 */

// ---------------------------------------------------------------------------
// What the worker is given
// ---------------------------------------------------------------------------

/**
 * The kinds a merge may be proposed for (`sds-04n`, adjudicated).
 *
 * **`data_flow` is absent, and it is absent on a measurement rather than on
 * caution.** ZD-026 §1 keys a flow on its *resolved endpoint pair*, so flow
 * identity is a consequence of object identity: the corpus's two positive flow
 * pairs — Culverhay's `Booking Service -> Yard Controller` against
 * `booking service -> yard controllers` — merge on their own the moment the
 * endpoint reference resolves. Judging them separately would be a third fact
 * that can disagree with the two it is derived from, which is the shape ZD-017
 * refused for assessment state and ZD-027 rule 3 refused for per-extraction
 * crossing derivation. Measured, it also removes **67 of 130** candidate pairs
 * at no cost in reachable positives.
 *
 * **`trust_boundary` is present, including unnamed ones, and that is a
 * deliberate widening of ZD-026 §1.** That entry says an unnamed boundary never
 * merges because *"nothing identifies them"* and `separates` equality *"is an
 * accident as often as an identity"*. Both are true of a **key**, which can only
 * compare. A judged proposal reads the documents instead, and the corpus's one
 * instance — Culverhay's unnamed dashed rectangle against
 * `yard network / office network` — is exactly the case a key cannot reach and a
 * reader can. The widening is recorded rather than taken quietly, and the cost
 * of being wrong is the one ZD-045 §1 measured: a duplicate, or a merge nothing
 * downstream can see.
 */
export const IDENTITY_MERGE_KINDS = [
  'component',
  'actor',
  'asset',
  'trust_boundary',
  'existing_control',
] as const;
export const IdentityMergeKindSchema = z.enum(IDENTITY_MERGE_KINDS);
export type IdentityMergeKind = (typeof IDENTITY_MERGE_KINDS)[number];

/** Where a reference the worker is asked to resolve was written. */
export const RESOLUTION_ATTRIBUTES = [
  'flow_endpoint',
  'separates',
  'applies_to',
  'asset_ids',
  'resides_in',
] as const;
export const ResolutionAttributeSchema = z.enum(RESOLUTION_ATTRIBUTES);
export type ResolutionAttribute = (typeof RESOLUTION_ATTRIBUTES)[number];

/** One end of a proposal: an object as its own document states it. */
export const CandidateSideSchema = z.object({
  extraction_index: z.number().int().nonnegative(),
  local_id: NonEmptyStringSchema,
  /**
   * What the document calls it, or `null` for an unnamed trust boundary.
   *
   * Nullable rather than filled, on ZD-019's rule: a dashed rectangle with no
   * text has no name, and an invented one is indistinguishable downstream from
   * one a document supplied (ZD-023). `label` below is what a reader sees.
   */
  name: NonEmptyStringSchema.nullable(),
  /** Code-generated for display. Never stored, never matched on (ZD-023). */
  label: NonEmptyStringSchema,
  artefact_id: NonEmptyStringSchema,
  /** The document's own name for itself, so the prompt can say *"the LLD"*. */
  document: NonEmptyStringSchema,
  /**
   * The model's own facts, as text — never prose *about* them.
   *
   * Only attributes the extraction actually established appear. An attribute
   * nobody stated is absent rather than present as `unknown`, because the
   * distinction between *stated* and *unstated* is the one thing a judgement
   * most needs and the one an absence-marker collapses.
   */
  established: z.record(z.string(), z.string()),
  /**
   * The resolved text of every unit this side's provenance cites, with the
   * `Locator` itself (`IDENTITY_WORKER.md` §4).
   *
   * **The locator object rather than a rendering of it**, because the worker has
   * to hand one back in its citation and a model shown *"page 2"* would have to
   * reconstruct the shape. Copying is checkable; reconstructing is a second
   * place a citation can go wrong for a reason that has nothing to do with the
   * judgement.
   *
   * Resolved through `resolveCitation` against the real `DocumentIndex`, so the
   * worker sees **what the document says** rather than what an earlier worker
   * said about it (ZD-015, ZD-018).
   */
  evidence: z.array(z.object({ locator: LocatorSchema, text: NonEmptyStringSchema })),
});
export type CandidateSide = z.infer<typeof CandidateSideSchema>;

/**
 * One merge question: are these two objects the same thing?
 *
 * Three rules about the input, each closing a way the worker could be handed
 * the answer (`IDENTITY_WORKER.md` §4), and all three are properties of this
 * shape rather than instructions beside it:
 *
 *  - **Never within one extraction.** ZD-026 §1: an extraction's objects are
 *    distinct by construction, and second-guessing that overrules the only
 *    component that read the document. The selector forms cross-document pairs
 *    only, and `identity_pair_is_cross_document` re-asserts it on acceptance.
 *  - **No canonical ids, and no other pair's outcome.** Handing the model the
 *    merge set so far makes the result order-dependent, which breaks
 *    reproducibility before the temperature does.
 *  - **Evidence is resolved, not quoted from the extraction.**
 */
export const IdentityMergeCandidateSchema = z.object({
  /** Readable and built from the two labels, so a rename fails loudly. */
  candidate_id: NonEmptyStringSchema,
  kind: IdentityMergeKindSchema,
  left: CandidateSideSchema,
  right: CandidateSideSchema,
  /**
   * Which selector signals fired. Recorded rather than derived, because a
   * signal that selected nothing has to be visible as a declaration rather than
   * as a silence (ZD-035 §2).
   */
  signals: z.array(NonEmptyStringSchema).min(1),
});
export type IdentityMergeCandidate = z.infer<typeof IdentityMergeCandidateSchema>;

/**
 * One resolution question: what does this reference denote?
 *
 * **The whole candidate list, in one call.** See the module comment: a pairwise
 * form cannot express `no_object`, and `no_object` is the right answer for half
 * the corpus's population.
 */
export const IdentityResolutionCandidateSchema = z.object({
  candidate_id: NonEmptyStringSchema,
  /** The bare label the document wrote, verbatim. */
  reference: NonEmptyStringSchema,
  attribute: ResolutionAttributeSchema,
  /** What the reference was part of — the flow, the boundary, the asset. */
  context: NonEmptyStringSchema,
  extraction_index: z.number().int().nonnegative(),
  artefact_id: NonEmptyStringSchema,
  document: NonEmptyStringSchema,
  /** Where the reference itself appears, resolved. */
  evidence: z.array(z.object({ locator: LocatorSchema, text: NonEmptyStringSchema })),
  /** Every object the reference could denote. May be empty. */
  candidates: z.array(CandidateSideSchema),
});
export type IdentityResolutionCandidate = z.infer<typeof IdentityResolutionCandidateSchema>;

// ---------------------------------------------------------------------------
// What the worker returns
// ---------------------------------------------------------------------------

export const IDENTITY_MERGE_VERDICTS = ['same', 'different', 'cannot_tell'] as const;
export const IdentityMergeVerdictSchema = z.enum(IDENTITY_MERGE_VERDICTS);
export type IdentityMergeVerdict = (typeof IDENTITY_MERGE_VERDICTS)[number];

export const IDENTITY_RESOLUTION_VERDICTS = ['resolved', 'cannot_tell', 'no_object'] as const;
export const IdentityResolutionVerdictSchema = z.enum(IDENTITY_RESOLUTION_VERDICTS);
export type IdentityResolutionVerdict = (typeof IDENTITY_RESOLUTION_VERDICTS)[number];

/**
 * A merge proposal.
 *
 * Both citations are unconditionally required, and that is not the same call
 * the resolution schema makes below: here both sides are **declared objects**
 * whose documents named them, so there is always somewhere for each citation to
 * point. `cannot_tell` is first-class and is the honest answer for the case
 * ZD-026 was built around; it becomes an identity clarification through
 * `refusalQuestion`, which already exists and is already emitted for
 * deterministically-refused merges.
 */
export const IdentityMergeProposalSchema = z.object({
  verdict: IdentityMergeVerdictSchema,
  /** Why, in one sentence, for the reviewer UI and the clarification. */
  reasoning: NonEmptyStringSchema,
  /** Where in each document the object being judged actually appears. */
  left_citation: EvidenceCitationSchema,
  right_citation: EvidenceCitationSchema,
});
export type IdentityMergeProposal = z.infer<typeof IdentityMergeProposalSchema>;

/**
 * A resolution proposal, as a union discriminated on the verdict.
 *
 * **`IDENTITY_WORKER.md` §6 check 2 is verdict-conditional, and the
 * conditionality belongs here rather than in a check.** That check requires
 * both citations to resolve; it was written before ZD-047 §5 added `no_object`,
 * and a `no_object` proposal has no second side to cite — the verdict's whole
 * content is that there is nothing there. Demanding a citation for an object the
 * model has just said does not exist is ZD-019's manufactured assertion,
 * reached through an enum member added after the check that reads it.
 *
 * So the fields that only make sense for `resolved` exist only on `resolved`: a
 * model cannot name a candidate on a `no_object`, and cannot omit one on a
 * `resolved`. Both directions, structurally, which is the only form that
 * survives a caller who has not read this comment.
 */
const ResolutionBase = {
  reasoning: NonEmptyStringSchema,
  /** Where the reference itself appears. Required on every verdict. */
  reference_citation: EvidenceCitationSchema,
};

export const IdentityResolutionProposalSchema = z.discriminatedUnion('verdict', [
  z.object({
    verdict: z.literal('resolved'),
    ...ResolutionBase,
    /** Which candidate, by the `local_id` it was offered under. */
    candidate_local_id: NonEmptyStringSchema,
    /** Where that candidate is named in its own document. */
    candidate_citation: EvidenceCitationSchema,
  }),
  /** The documents do not settle which of the candidates it is. */
  z.object({ verdict: z.literal('cannot_tell'), ...ResolutionBase }),
  /** The thing this names is not in the model — ZD-027 rule 1's drop-and-ask. */
  z.object({ verdict: z.literal('no_object'), ...ResolutionBase }),
]);
export type IdentityResolutionProposal = z.infer<typeof IdentityResolutionProposalSchema>;

// ---------------------------------------------------------------------------
// What an accepted decision becomes
// ---------------------------------------------------------------------------

/**
 * A reference `normalise` could not place (ZD-027 rule 1, ZD-047 §5).
 *
 * **Returned by the resolver rather than re-derived beside it**, and that is not
 * tidiness. Working out which references dangle means reproducing `resolve` —
 * the local-id branch, the identity key, the ambiguity refusal, and the retry of
 * a set member against every extraction its object was merged from. A second
 * implementation of that is two rules (ZD-027 rule 3); it would disagree with
 * the real one exactly where the real one is subtle, and it would do so
 * silently, because a worker asked about a reference that in fact resolves is
 * being asked to judge an input the pipeline invented — which is the thing
 * `IDENTITY_WORKER.md` §6 check 7 exists to refuse.
 *
 * So `normalise` says what it could not place, and the worker is asked about
 * that and nothing else.
 */
export interface UnresolvedReferenceRecord {
  extraction_index: number;
  /** The reference string, verbatim as the extraction wrote it. */
  reference: string;
  attribute: ResolutionAttribute;
  /** Where it was written — the flow, boundary, asset or control. */
  context: string;
  /**
   * The local id of the object it was written on.
   *
   * **This is what the reference's own evidence is resolved from**, and the
   * alternative is worse than it looks: searching the document for the reference
   * string returns nothing whenever the transcription differs from the prose by
   * a word — measured, `the staff browser` finds nothing in Tarnmouth's HLD — and
   * a worker shown no locators cannot satisfy the one check that does real work
   * on this half. The object carries citations that resolve; the string does not.
   */
  local_id: string;
  /** The kinds `resolve` searched, which is what the candidate list is drawn from. */
  kinds: string[];
}

/**
 * One extracted object, named by where it was extracted from.
 *
 * **Not by identity key, and the unnamed boundary is why.** A named object's
 * key is a function of its name, but `normalise` keys an unnamed boundary — and
 * every member of a refused group — *positionally* (ZD-049 §1), so a judgement
 * expressed in keys could not name half of what it is now permitted to judge.
 * `(extraction_index, local_id)` is what the worker was actually shown, it is
 * unambiguous for every kind, and it leaves `normalise` to compute the keys,
 * which is where they are computed already.
 */
export interface JudgedMember {
  extraction_index: number;
  local_id: string;
}

/** Objects a judged proposal says denote one thing. */
export interface JudgedIdentity {
  kind: IdentityMergeKind;
  /** At least two, and never two from one extraction (ZD-026 §1). */
  members: JudgedMember[];
}

/** A reference a judged proposal resolved to an object. */
export interface JudgedResolution {
  extraction_index: number;
  /** The reference string, verbatim as the extraction wrote it. */
  reference: string;
  /** What it denotes. */
  target: JudgedMember;
}

/**
 * What `normalise` is told a judged run decided.
 *
 * **Required rather than optional-with-a-default**, for the reason ZD-049 gives
 * for the identity ledger: a caller that omitted it would silently drop every
 * judgement, in the direction ZD-045 §1 established nothing downstream can see.
 * *No judgement* has to be something a caller says, not something it forgets —
 * so `NO_JUDGEMENT` is exported and is what a deterministic run passes.
 */
export interface IdentityJudgement {
  identities: readonly JudgedIdentity[];
  resolutions: readonly JudgedResolution[];
}

export const NO_JUDGEMENT: IdentityJudgement = { identities: [], resolutions: [] };
